import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { GraduationCap, LogOut, Menu, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import AIMentor from "@/components/AIMentor";
import { Button } from "@/components/ui/button";

const ROLE_LABEL = {
    student: "Student", teacher: "Teacher", mentor: "Mentor", parent: "Parent",
    owner: "Owner", super_admin: "Admin",
};

export default function DashboardLayout({ nav = [], active, onNav, children, title }) {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);

    const doLogout = async () => { await logout(); navigate("/"); };

    return (
        <div className="min-h-screen flex bg-muted/30">
            {/* Sidebar */}
            <aside className={`fixed lg:sticky top-0 z-40 h-screen w-64 shrink-0 bg-secondary text-secondary-foreground flex flex-col transition-transform ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
                <div className="flex items-center justify-between h-16 px-5 border-b border-white/10">
                    <Link to="/" className="flex items-center gap-2"><span className="grid h-8 w-8 place-items-center rounded-lg bg-primary text-primary-foreground"><GraduationCap className="h-4 w-4" /></span><span className="font-heading font-extrabold">LARUTA<span className="text-primary">.</span></span></Link>
                    <button className="lg:hidden" onClick={() => setOpen(false)}><X className="h-5 w-5" /></button>
                </div>
                <nav className="flex-1 overflow-y-auto p-3 space-y-1">
                    {nav.map((n) => (
                        <button key={n.key} data-testid={`nav-${n.key}`} onClick={() => { onNav?.(n.key); setOpen(false); }}
                            className={`w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${active === n.key ? "bg-primary text-primary-foreground" : "text-secondary-foreground/70 hover:bg-white/5"}`}>
                            <n.icon className="h-4 w-4 shrink-0" /> {n.label}
                        </button>
                    ))}
                </nav>
                <div className="p-3 border-t border-white/10">
                    <div className="flex items-center gap-3 px-2 py-2">
                        <span className="grid h-9 w-9 place-items-center rounded-full bg-primary/20 text-primary font-heading font-bold text-sm">{(user?.name || "U")[0]}</span>
                        <div className="min-w-0"><p className="text-sm font-medium truncate">{user?.name}</p><p className="text-xs text-secondary-foreground/50 capitalize">{ROLE_LABEL[user?.role] || user?.role}</p></div>
                    </div>
                    <Button variant="ghost" onClick={doLogout} data-testid="logout-btn" className="w-full justify-start text-secondary-foreground/70 hover:text-secondary-foreground hover:bg-white/5"><LogOut className="mr-2 h-4 w-4" /> Log out</Button>
                </div>
            </aside>
            {open && <div className="fixed inset-0 z-30 bg-black/40 lg:hidden" onClick={() => setOpen(false)} />}

            {/* Main */}
            <div className="flex-1 min-w-0 flex flex-col">
                <header className="sticky top-0 z-20 flex items-center gap-3 h-16 px-4 sm:px-8 bg-background/85 backdrop-blur-xl border-b border-border">
                    <button className="lg:hidden" onClick={() => setOpen(true)} data-testid="sidebar-toggle"><Menu className="h-6 w-6" /></button>
                    <h1 className="font-heading text-lg font-bold truncate">{title}</h1>
                </header>
                <div className="flex-1 p-4 sm:p-8">{children}</div>
            </div>
            <AIMentor />
        </div>
    );
}

export function StatCard({ icon: Icon, label, value, sub, color = "primary" }) {
    return (
        <div className="rounded-2xl bg-card border border-border p-5" data-testid={`stat-${label.toLowerCase().replace(/ /g, "-")}`}>
            <div className="flex items-center justify-between">
                <span className={`grid h-10 w-10 place-items-center rounded-xl bg-${color}/10 text-${color}`}><Icon className="h-5 w-5" /></span>
            </div>
            <p className="mt-4 font-heading text-2xl font-extrabold">{value}</p>
            <p className="text-sm text-foreground/50">{label}</p>
            {sub && <p className="text-xs text-foreground/40 mt-1">{sub}</p>}
        </div>
    );
}

export function EmptyState({ icon: Icon, title, desc }) {
    return (
        <div className="rounded-2xl border border-dashed border-border p-12 text-center">
            <span className="grid h-12 w-12 mx-auto place-items-center rounded-full bg-muted text-foreground/40"><Icon className="h-6 w-6" /></span>
            <p className="mt-4 font-heading font-semibold">{title}</p>
            <p className="mt-1 text-sm text-foreground/50">{desc}</p>
        </div>
    );
}
